import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Cloud,
  CreditCard,
  FileText,
  Flag,
  Landmark,
  LayoutDashboard,
  LineChart,
  Mail,
  Package,
  PercentDiamond,
  Plug,
  Settings,
  ScrollText,
  Shield,
  Sparkles,
  Tags,
  Users,
  Coins,
  X,
} from 'lucide-react';
import { cn } from '@gelirgider/ui';

interface NavItem {
  to: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
}

const items: NavItem[] = [
  { to: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { to: '/users', labelKey: 'nav.users', icon: Users },
  { to: '/plans', labelKey: 'nav.plans', icon: Package },
  { to: '/coupons', labelKey: 'nav.coupons', icon: PercentDiamond },
  { to: '/invoices', labelKey: 'nav.invoices', icon: FileText },
  { to: '/exchange-rates', labelKey: 'nav.exchangeRates', icon: Coins },
  { to: '/tags', labelKey: 'nav.tags', icon: Tags },
  { to: '/activity-logs', labelKey: 'nav.activityLogs', icon: ScrollText },
  { to: '/site-settings', labelKey: 'nav.siteSettings', icon: Settings },
  { to: '/benefit-cards', labelKey: 'nav.benefitCards', icon: CreditCard },
  { to: '/billing-metrics', labelKey: 'nav.billingMetrics', icon: LineChart },
  { to: '/stripe-settings', labelKey: 'nav.stripeSettings', icon: Plug },
  { to: '/open-banking-settings', labelKey: 'nav.openBanking', icon: Landmark },
  { to: '/integrations-settings', labelKey: 'nav.integrations', icon: Plug },
  { to: '/storage-settings', labelKey: 'nav.storage', icon: Cloud },
  { to: '/ai-settings', labelKey: 'nav.aiSettings', icon: Sparkles },
  { to: '/mail-logs', labelKey: 'nav.mailLogs', icon: Mail },
  { to: '/template-reports', labelKey: 'nav.templateReports', icon: Flag },
];

interface SidebarProps {
  mobile?: boolean;
  onNavigate?: () => void;
}

export function Sidebar({ mobile = false, onNavigate }: SidebarProps = {}): React.ReactElement {
  const { t } = useTranslation('admin');
  return (
    <aside
      className={cn(
        'shrink-0 border-r bg-card flex-col',
        mobile ? 'flex w-full h-full' : 'hidden w-64 md:flex',
      )}
    >
      <div className="flex h-16 items-center justify-between border-b px-6">
        <div className="flex items-center gap-2">
          <Shield className="h-6 w-6 text-primary" />
          <span className="text-lg font-semibold">{t('topbar.title')}</span>
        </div>
        {mobile && onNavigate ? (
          <button
            type="button"
            aria-label={t('topbar.closeMenu')}
            className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            onClick={onNavigate}
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto p-4">
        {items.map(({ to, labelKey, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )
            }
          >
            <Icon className="h-4 w-4" />
            <span>{t(labelKey)}</span>
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}
